import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useItem } from '../api/hooks';
import type { ItemDetail } from '../api/types';
import { SensitivityBadge, TypeTag } from '../components/Badges';
import { ErrorPanel, Loading } from '../components/ErrorPanel';
import { FileRow } from '../components/FileRow';
import { Icon } from '../components/Icon';
import { formatDate } from '../format';
import {
  dataNature,
  labelOf,
  payloadCategories,
  pilots,
  qualityLevels,
  sectors,
  sensitivity,
  structureTypes,
  updateFrequency,
  validationStatus,
} from '../labels/labels';
import { strings } from '../strings';

const str = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

/** Files attached to an item: media types use artifactId, datasets a dataset and a payload file. */
export function attachedFiles(item: ItemDetail): { id: string; role: string }[] {
  const files: { id: string; role: string }[] = [];
  const add = (id: unknown, role: string) => {
    const value = str(id);
    if (value && !files.some((f) => f.id === value)) files.push({ id: value, role });
  };
  add(item.payloadArtifactId, strings.item.payloadFile);
  add(item.datasetArtifactId, strings.item.datasetFile);
  add(item.artifactId, strings.item.attachedFile);
  return files;
}

function Fact({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="card">
      <h5 className="m-0">{title}</h5>
      <div className="mt-2 space-y-1">{children}</div>
    </section>
  );
}

function DatasetSummary({ item }: { item: ItemDetail }) {
  const level = str(item.sensitivityLevel);
  const category = str(item.payloadCategory);
  const structure = str(item.payloadStructureType);
  const nature = str(item.dataNature);
  const custodian = str(item.datasetCustodianName);
  const custodianOrg = str(item.datasetCustodianOrganization);
  const custodianEmail = str(item.datasetCustodianEmail);
  const start = formatDate(item.temporalCoverageStart);
  const end = formatDate(item.temporalCoverageEnd);
  const quality = str(item.qualityLevel);
  const validation = str(item.validationStatus);
  const frequency = str(item.updateFrequency);
  const pilot = str(item.pilot);
  const sector = str(item.sector);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Fact title={strings.item.whatIsIt}>
        {category && (
          <p className="m-0" title={payloadCategories[category]?.help}>
            {labelOf(payloadCategories, category)}
            {structure && structure !== category ? ` — ${labelOf(structureTypes, structure)}` : ''}
          </p>
        )}
        {nature && <p className="m-0 text-small text-neutral-muted">{dataNature[nature]?.help ?? labelOf(dataNature, nature)}</p>}
        {(pilot || sector) && (
          <p className="m-0 text-small">{[labelOf(pilots, pilot), labelOf(sectors, sector)].filter(Boolean).join(' · ')}</p>
        )}
        {!category && !nature && !pilot && <p className="m-0">{strings.item.notStated}</p>}
      </Fact>

      <Fact title={strings.item.whoLooksAfterIt}>
        {custodian || custodianOrg ? (
          <>
            <p className="m-0">{[custodian, custodianOrg].filter(Boolean).join(', ')}</p>
            {custodianEmail && (
              <p className="m-0">
                <a href={`mailto:${custodianEmail}`}>{strings.item.contactCustodian}</a>
              </p>
            )}
          </>
        ) : (
          <p className="m-0">{strings.item.notStated}</p>
        )}
      </Fact>

      <Fact title={strings.item.howSensitive}>
        {level ? (
          <>
            <SensitivityBadge level={level} />
            {sensitivity[level]?.help && <p className="m-0">{sensitivity[level].help}</p>}
          </>
        ) : (
          <p className="m-0">{strings.item.notStated}</p>
        )}
        {item.containsPersonalData === true && <p className="m-0 font-bold">{strings.item.personalData}</p>}
        {item.criticalInfrastructureData === true && <p className="m-0 font-bold">{strings.item.criticalInfrastructure}</p>}
      </Fact>

      <Fact title={strings.item.coverage}>
        {start || end ? (
          <p className="m-0">
            {strings.item.from} {start || '…'} {strings.item.to} {end || '…'}
          </p>
        ) : (
          <p className="m-0">{strings.item.notStated}</p>
        )}
        {frequency && (
          <p className="m-0 text-small">
            {strings.item.updates}: {labelOf(updateFrequency, frequency)}
          </p>
        )}
      </Fact>

      <Fact title={strings.item.quality}>
        {quality ? (
          <p className="m-0" title={qualityLevels[quality]?.help}>
            {labelOf(qualityLevels, quality)}
            {validation ? ` · ${labelOf(validationStatus, validation)}` : ''}
          </p>
        ) : (
          <p className="m-0">{strings.item.notStated}</p>
        )}
        {str(item.version) && (
          <p className="m-0 text-small">
            {strings.item.version} {str(item.version)}
          </p>
        )}
      </Fact>
    </div>
  );
}

function prettyHeader(item: ItemDetail): string | undefined {
  const raw = str(item.headerMetadata);
  if (!raw) return undefined;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function Item() {
  const { tenant = '', id = '' } = useParams();
  const navigate = useNavigate();
  const query = useItem(tenant, id);

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  const item = query.data;
  const isDataset = item.type.toLowerCase() === 'dataset';
  const files = attachedFiles(item);
  const header = prettyHeader(item);
  const updated = formatDate(item.updated_at);

  return (
    <article className="space-y-8">
      <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
        <Icon name="arrowLeft" />
        {strings.item.back}
      </button>

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <TypeTag type={item.type} />
          {isDataset && <SensitivityBadge level={str(item.sensitivityLevel)} />}
        </div>
        <h1 className="m-0">{item.name || item.id}</h1>
        {item.description && <p className="max-w-3xl whitespace-pre-line">{item.description}</p>}
        {updated && <p className="m-0 text-small text-neutral-muted">{strings.search.updated(updated)}</p>}
      </header>

      {isDataset && <DatasetSummary item={item} />}

      <section aria-labelledby="files-title">
        <h2 id="files-title">{strings.item.files}</h2>
        {files.length === 0 ? (
          <p>{strings.item.noFiles}</p>
        ) : (
          <ul className="m-0 list-none space-y-3 p-0">
            {files.map((f) => (
              <FileRow
                key={f.id}
                tenant={tenant}
                artifactId={f.id}
                role={f.role}
                itemSensitivity={isDataset ? str(item.sensitivityLevel) : undefined}
              />
            ))}
          </ul>
        )}
      </section>

      {header && (
        <details className="card">
          <summary className="cursor-pointer font-bold">{strings.item.technical}</summary>
          <p className="text-small text-neutral-muted">{strings.item.technicalIntro}</p>
          <pre className="max-h-[32rem] overflow-auto bg-neutral-surface p-4 text-small">{header}</pre>
        </details>
      )}
    </article>
  );
}
